import * as THREE from 'three';
import { TERRAIN } from '../config';
import type { SkyUniforms } from './scene';

// Volumetric-ish CLOUD LAYER: a big flat follow-plane high above the world, shaded by a multi-octave fbm that
// both SCROLLS (drift) and EVOLVES (the higher octaves shift over time, so clouds morph/change). Soft puffy
// coverage with transparent gaps (blue sky shows through), darker undersides + sun-lit tops for fake volume.
// One plane, a handful of noise taps per pixel -> cheap. Shares the sky's day-night uniforms so it dims at night.
// The SAME field drives the ground cloud-shadows in the terrain + grass shaders (see cloudFieldGLSL), so the
// shadows on the ground match the clouds in the sky.

const CLOUD_Y = 240;       // height above sea level
const CIRRUS_Y = 450;      // the high thin layer (parallax against the low deck)
const CLOUD_SIZE = 6000;   // follow-plane extent (well past the fog so it fills the upper sky)

// Shared cloud-shadow uniforms -- ONE source for every surface that samples the shadow (world clock + live sun
// direction). Updated once per frame from main; all the cloud-shadow shaders point at these, so the shadow on
// the ground, grass, trees, water + rocks all match the clouds in the sky AND each other.
export const cloudUniforms = {
  uTime: { value: 0 }, uSunDir: { value: new THREE.Vector3(0, 1, 0) }, uCloudCover: { value: 0 }, uStorm: { value: 0 },
  // Pre-storm APPROACH (a storm front rolling in from the horizon). uStormCover = extra coverage to add on the
  // storm side, uStormFront 0..1 = how far the front has swept in (0 far horizon, 1 overhead), uStormDir = the
  // bearing it comes from (unit XZ), uPlayerXZ = the player's world XZ the front sweeps toward.
  uStormDir: { value: new THREE.Vector2(0, 1) }, uStormFront: { value: 0 }, uStormCover: { value: 0 }, uPlayerXZ: { value: new THREE.Vector2(0, 0) },
  // Latest lightning strike for the INTRA-CLOUD bloom: (x, z, intensity, radius). main sets it on each
  // strike and decays intensity every frame, so the deck pulses with light around the bolt.
  uFlash: { value: new THREE.Vector4(0, 0, 0, 480) },
};
// uCloudCover: a weather-driven BIAS added to the cloud density (sunny < 0 = clearer sky, overcast > 0 = more
// clouds + bigger shadows). Set each frame by the weather system; every cloud shader reads it so sky + shadows
// thicken/thin together.

// Shared cloud-DENSITY field (world XZ -> 0..1), reused verbatim by the ground-shadow shaders so the drifting
// shadows line up with these clouds. Caller provides `uTime`.
export const cloudFieldGLSL = `
  // precision-safe hash (wrapped lattice + sin-free): sin(1e5+) range-reduces badly on some GPUs and
  // quantizes into visible structure. SAME signature -- only the body changed (shared-GLSL rule).
  float oxCH(vec2 p){ p = mod(floor(p), 1024.0); vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  float oxCN(vec2 p){ vec2 i=floor(p),f=fract(p); f=f*f*(3.0-2.0*f);
    float a=oxCH(i),b=oxCH(i+vec2(1.,0.)),c=oxCH(i+vec2(0.,1.)),d=oxCH(i+vec2(1.,1.));
    return mix(mix(a,b,f.x),mix(c,d,f.x),f.y); }
  float oxCloud(vec2 wxz, float t){
    vec2 uv = wxz * 0.0016 + vec2(t * 0.016, t * 0.010);     // drift (clearly visible: ~10 m/s)
    float s = 0.0, a = 0.5;
    for (int i = 0; i < 5; i++){ s += a * oxCN(uv); uv = uv * 2.02 + 0.05 * t * a; a *= 0.5; } // + slow evolve (morph)
    return s;
  }
  // Cloud SHADOW (light factor: 1 lit .. 0.38 shadowed) at ground point wxz. Projects the cloud (height ~240m)
  // DOWN the sun ray (offset by sunDir.xz * H / sunDir.y) so the shadow sits under the cloud you SEE and
  // slides/stretches as the sun moves. A tight threshold gives DEFINED, contrasty patches -- a small dense puff
  // drops a crisp dark shadow, while a big bank still shades broadly.
  float oxCloudShadow(vec2 wxz, vec3 sunDir, float t, float cover){
    vec2 p = wxz + sunDir.xz * (240.0 / max(sunDir.y, 0.18));
    // Shadow contrast fades as the sky thickens: a heavy overcast (high cover) scatters the light, so there is
    // little direct sun to cast a hard shadow -> the patches go soft. Sunny/scattered (cover<=0) stays strong:
    // a passing puff drops the ground to ~25% light under its core (owner ask: OBVIOUS travelling patches, not
    // a gentle global dim), with the smoothstep band keeping the edge a soft ~60-100m penumbra.
    float cd = oxCloud(p, t) + cover;
    // Heavy overcast: the shadow term must COLLAPSE, not settle at a flat dark multiplier -- at storm
    // cover the smoothstep saturates EVERYWHERE, so any residual strength was a uniform darkening applied
    // only to props (barrels/trees/rocks read pitch black while terrain stayed lit -- owner bug 2026-07-12).
    float strength = 0.75 * (1.0 - 0.85 * clamp(cover * 2.0, 0.0, 1.0));
    float lit = 1.0 - strength * smoothstep(0.44, 0.56, cd);
    // SUN POOLS: the inverse threshold -- fully-clear gaps get a slight warm boost, strongest on
    // broken/heavy skies, so pools of sunlight visibly race across dark land between the shadows.
    lit *= 1.0 + 0.16 * (1.0 - smoothstep(0.30, 0.42, cd)) * clamp(cover * 3.0 + 0.25, 0.0, 1.0);
    return lit;
  }`;

// Inject a moving, sun-projected cloud SHADOW into any LIT material (trees/rocks/bushes/...): adds a world-XZ
// varying (instancing-aware) + the shared field, and multiplies the lit colour by oxCloudShadow right after the
// surface colour is resolved (before tone-map/fog). Composes with the material's existing onBeforeCompile +
// cache key. `key` must be unique per material so shader programs don't collide.
export function applyCloudShadow(mat: THREE.Material, key: string): void {
  const prevCompile = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey;
  mat.onBeforeCompile = (shader, renderer) => {
    prevCompile.call(mat, shader, renderer); // bind `this`: a fresh material's default hooks read this.* and throw if unbound
    shader.uniforms.uTime = cloudUniforms.uTime;
    shader.uniforms.uSunDir = cloudUniforms.uSunDir;
    shader.uniforms.uCloudCover = cloudUniforms.uCloudCover;
    shader.uniforms.uStorm = cloudUniforms.uStorm;
    shader.vertexShader = 'varying vec2 vCloudXZ;\n' + shader.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
       vec4 oxCWP = vec4(transformed, 1.0);
       #ifdef USE_INSTANCING
         oxCWP = instanceMatrix * oxCWP;
       #endif
       vCloudXZ = (modelMatrix * oxCWP).xz;`);
    shader.fragmentShader = ('uniform float uTime;\nuniform vec3 uSunDir;\nuniform float uCloudCover;\nuniform float uStorm;\nvarying vec2 vCloudXZ;\n' + cloudFieldGLSL + '\n' + shader.fragmentShader).replace(
      '#include <opaque_fragment>',
      `#include <opaque_fragment>
       // PROPS FLOOR (owner law 2026-07-12: objects are NEVER black). The full-strength shadow (~25% light
       // under a puff core) is for the GROUND, where it reads as a shadow patch -- on an already-dark tree
       // trunk or barrel it reads as a black silhouette. Props keep at least 60% of their lit colour.
       gl_FragColor.rgb *= max(oxCloudShadow(vCloudXZ, uSunDir, uTime, uCloudCover), 0.6);
       // WET GROUND: under active rain (dense cloud overhead + a storm), darken + cool the surface so it reads wet.
       float oxWet = smoothstep(0.42, 0.62, oxCloud(vCloudXZ, uTime) + uCloudCover) * clamp(uStorm, 0.0, 1.0);
       gl_FragColor.rgb *= (1.0 - 0.16 * oxWet); // wetness reads mostly via the cool tint below, not blackness
       gl_FragColor.rgb = mix(gl_FragColor.rgb, gl_FragColor.rgb * vec3(0.82, 0.90, 1.06), 0.45 * oxWet);`);
  };
  mat.customProgramCacheKey = () => prevKey.call(mat) + '|cloudshadow3wet-' + key; // .call(mat): default cacheKey reads this.onBeforeCompile
}

export class CloudLayer {
  readonly mesh: THREE.Mesh;
  readonly cirrus: THREE.Mesh; // high thin second layer (main adds it to the scene alongside mesh)

  constructor(sky: SkyUniforms) {
    const geom = new THREE.PlaneGeometry(CLOUD_SIZE, CLOUD_SIZE, 1, 1);
    geom.rotateX(-Math.PI / 2);
    const mat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = sky.uTime;
      shader.uniforms.uSunDir = sky.uSunDir;
      shader.uniforms.uCloud = sky.uCloud;
      shader.uniforms.uSunColor = sky.uSunColor;
      shader.uniforms.uCloudCover = cloudUniforms.uCloudCover;
      shader.uniforms.uStormDir = cloudUniforms.uStormDir;
      shader.uniforms.uStormFront = cloudUniforms.uStormFront;
      shader.uniforms.uStormCover = cloudUniforms.uStormCover;
      shader.uniforms.uPlayerXZ = cloudUniforms.uPlayerXZ;
      shader.uniforms.uFlash = cloudUniforms.uFlash;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\n varying vec3 vWPos;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n vWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
          varying vec3 vWPos; uniform float uTime; uniform vec3 uSunDir, uCloud, uSunColor; uniform float uCloudCover;
          uniform vec2 uStormDir, uPlayerXZ; uniform float uStormFront, uStormCover; uniform vec4 uFlash;
          ${cloudFieldGLSL}`)
        .replace('#include <color_fragment>', `#include <color_fragment>
          {
            vec2 toCamXZ = vWPos.xz - cameraPosition.xz;
            float rad = length(toCamXZ);
            // DISTANCE LOD: far away the cloud-field detail is sub-pixel and ALIASES into sharp speckle (very
            // visible once a storm darkens the clouds). Fade the noisy detail out with range and blend toward a
            // SMOOTH UNIFORM overcast (the field's average) -- near clouds keep full detail, distant clouds are a
            // clean smooth sheet, so there's nothing fine enough to alias.
            float detailFade = smoothstep(300.0, 1100.0, rad);
            float d = mix(oxCloud(vWPos.xz, uTime) + uCloudCover, 0.5 + uCloudCover, detailFade);

            // STORM FRONT (approach): a storm gathers from one horizon and rolls toward the player. Add coverage
            // on the storm side of a line that sweeps inward as uStormFront grows (0 = far horizon, 1 = overhead).
            float along = dot(vWPos.xz - uPlayerXZ, uStormDir);            // +ve toward the storm-bearing horizon
            float frontPos = mix(2600.0, -1400.0, uStormFront);             // leading edge: far horizon -> sweeps past you
            float frontMask = smoothstep(frontPos - 700.0, frontPos + 700.0, along); // cloudy on the FAR side of the edge
            d += uStormCover * frontMask;                                   // thicken the approaching bank

            float cov = smoothstep(0.40, 0.62, d); // coverage; gaps stay clear sky (partial, not overcast)

            // HORIZON DISSOLVE: the sheet is a flat follow-plane, so its far rectangular edge would cut off as a
            // hard line at the horizon. Fade the cloud out over the OUTER part of the plane (radial) AND as the
            // view ray to this fragment grazes horizontal (angle), so it melts into the sky-dome horizon instead.
            float radialFade = 1.0 - smoothstep(1600.0, 2700.0, rad);   // dissolve before the ~3000m geometric edge
            float dy = vWPos.y - cameraPosition.y;
            float elev = abs(dy) / max(rad, 1.0);                       // tan(view-ray elevation); small == grazing. abs() -> works from any eye height
            float grazeFade = smoothstep(0.045, 0.16, elev);           // 0 at the horizon, 1 overhead
            cov *= radialFade * grazeFade;

            if (cov < 0.012) discard;
            float day = clamp(uSunDir.y * 3.0 + 0.25, 0.0, 1.0);
            vec3 col = mix(uCloud * 0.55, uCloud, smoothstep(0.5, 0.95, d)); // dark base -> bright crown = fake volume
            col += uSunColor * smoothstep(0.72, 1.0, d) * 0.3 * day;          // sun-lit tops
            // SILVER LINING: one extra field tap a step toward the sun -- where density FALLS sunward,
            // this fragment is a lit puff edge. Fades with the same distance LOD (no far shimmer).
            vec2 sunXZ = normalize(uSunDir.xz + vec2(1e-4, 0.0));
            float dS = mix(oxCloud(vWPos.xz + sunXZ * 130.0, uTime) + uCloudCover, 0.5 + uCloudCover, detailFade);
            col += uSunColor * clamp((d - dS) * 2.6, 0.0, 1.0) * smoothstep(0.38, 0.55, d) * (1.0 - detailFade) * 0.55 * day;
            // LOW-SUN BELLIES: dawn/dusk light washes the undersides warm (uSunColor is already orange then).
            float lowSun = (1.0 - smoothstep(0.10, 0.38, abs(uSunDir.y))) * day;
            col = mix(col, uSunColor, lowSun * smoothstep(0.48, 0.8, d) * 0.4);
            // SUN GLOW THROUGH THE DECK: where the view ray runs toward the sun the deck glows --
            // blinding through thin cover, a dull bright patch through thick (you can always tell
            // where the sun is behind the clouds).
            vec3 vdir = normalize(vWPos - cameraPosition);
            float sunAlign = max(dot(vdir, normalize(uSunDir)), 0.0);
            float thin = 1.0 - smoothstep(0.42, 0.85, d);
            col += uSunColor * (pow(sunAlign, 90.0) * (0.35 + 1.5 * thin) + pow(sunAlign, 10.0) * 0.10) * day;
            col = mix(col, uCloud * 0.4, uStormCover * frontMask * 0.6);      // the leading storm bank reads darker
            col *= mix(0.16, 1.0, day);                                       // dim at night
            // INTRA-CLOUD LIGHTNING: a brief bloom in the deck around the latest bolt (after the night
            // dim on purpose -- night strikes light the clouds from inside).
            col += vec3(0.85, 0.92, 1.15) * uFlash.z * exp(-length(vWPos.xz - uFlash.xy) / max(uFlash.w, 1.0)) * smoothstep(0.3, 0.55, d);
            diffuseColor = vec4(col, cov * 0.92);
          }`);
    };
    mat.customProgramCacheKey = () => 'ox-clouds-v8-lit';
    this.mesh = new THREE.Mesh(geom, mat);
    this.mesh.position.y = TERRAIN.seaLevel + CLOUD_Y;
    this.mesh.name = 'clouds';
    this.mesh.renderOrder = 1;
    this.mesh.frustumCulled = false;

    // HIGH CIRRUS: a second, much thinner sheet at 450m -- streaky stretched noise on its own slower
    // heading. Two layers sliding at different speeds give the sky real parallax depth as you move,
    // and cirrus catches uSunColor hard at dawn/dusk (it lights up before/after the ground does).
    const cgeom = new THREE.PlaneGeometry(CLOUD_SIZE, CLOUD_SIZE, 1, 1);
    cgeom.rotateX(-Math.PI / 2);
    const cmat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    cmat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = sky.uTime;
      shader.uniforms.uSunDir = sky.uSunDir;
      shader.uniforms.uCloud = sky.uCloud;
      shader.uniforms.uSunColor = sky.uSunColor;
      shader.uniforms.uCloudCover = cloudUniforms.uCloudCover;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\n varying vec3 vWPos;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n vWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
          varying vec3 vWPos; uniform float uTime; uniform vec3 uSunDir, uCloud, uSunColor; uniform float uCloudCover;
          ${cloudFieldGLSL}`)
        .replace('#include <color_fragment>', `#include <color_fragment>
          {
            vec2 toCamXZ = vWPos.xz - cameraPosition.xz;
            float rad = length(toCamXZ);
            // Streaky stretched fbm, 3 octaves, drifting slower than the low deck (parallax).
            vec2 q = vWPos.xz * vec2(0.0009, 0.0034) + vec2(uTime * 0.0060, uTime * 0.0016);
            float s = 0.0, a = 0.5;
            for (int i = 0; i < 3; i++){ s += a * oxCN(q); q = q * 2.13 + 11.7; a *= 0.55; }
            s = mix(s, 0.42, smoothstep(500.0, 1600.0, rad)); // distance LOD: far cirrus goes smooth (no shimmer)
            float cv = smoothstep(0.50, 0.80, s + uCloudCover * 0.4) * 0.4;
            float dy = vWPos.y - cameraPosition.y;
            float elev = abs(dy) / max(rad, 1.0);
            cv *= (1.0 - smoothstep(1700.0, 2700.0, rad)) * smoothstep(0.05, 0.17, elev); // melt at range + horizon
            if (cv < 0.01) discard;
            float day = clamp(uSunDir.y * 3.0 + 0.25, 0.0, 1.0);
            float lowSun = 1.0 - smoothstep(0.10, 0.36, abs(uSunDir.y));
            vec3 col = mix(uCloud, uSunColor, clamp(lowSun * 1.1, 0.0, 0.85)); // catches fire at dawn/dusk
            col *= mix(0.16, 1.0, day);
            diffuseColor = vec4(col, cv);
          }`);
    };
    cmat.customProgramCacheKey = () => 'ox-cirrus-v1';
    this.cirrus = new THREE.Mesh(cgeom, cmat);
    this.cirrus.position.y = TERRAIN.seaLevel + CIRRUS_Y;
    this.cirrus.name = 'cirrus';
    this.cirrus.renderOrder = 1;
    this.cirrus.frustumCulled = false;
  }

  // Follow the camera focus in XZ so the sheets always fill the sky above the player.
  update(fx: number, fz: number): void {
    this.mesh.position.x = fx; this.mesh.position.z = fz;
    this.cirrus.position.x = fx; this.cirrus.position.z = fz;
  }
}
